import { createElement } from 'react';
import type { ComponentPropsWithoutRef, ElementType, ReactNode, Ref } from 'react';

import { Button as DesignSystemButton } from 'design-system';

import { cn } from '@/lib/utils';
import type { Sx } from '@/shared/types/uiTypes';

export type ButtonSize = 'xsmall' | 'small' | 'compact' | 'normal' | 'medium' | 'large';
type LegacyVariant = 'contained' | 'outlined' | 'text';

const sizeClassName: Record<ButtonSize, string> = {
  xsmall: 'h-[22px] px-2 text-xs',
  small: 'h-[30px] px-3 text-sm',
  compact: 'h-7 px-2 text-xs',
  normal: 'h-10 px-3 text-base',
  medium: 'h-11 px-3 text-sm',
  large: 'h-12 px-3 text-base'
};

const variantMap: Record<LegacyVariant, 'default' | 'outline' | 'ghost'> = {
  contained: 'default',
  outlined: 'outline',
  text: 'ghost'
};

export type ButtonProps<C extends ElementType = ElementType> = Omit<
  ComponentPropsWithoutRef<C>,
  'children' | 'color' | 'size'
> & {
  children: ReactNode;
  size?: ButtonSize;
  variant?: LegacyVariant;
  component?: C;
  color?: 'primary' | 'secondary' | 'error' | 'inherit' | string;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  sx?: Sx;
};

export const Button = <C extends ElementType = ElementType>({
  children,
  className,
  component,
  endIcon,
  fullWidth,
  loading,
  size = 'normal',
  startIcon,
  sx: _sx,
  variant = 'contained',
  ...rest
}: ButtonProps<C> & { ref?: Ref<HTMLButtonElement | HTMLAnchorElement> }) => {
  const content = (
    <>
      {startIcon && <span data-icon='inline-start'>{startIcon}</span>}
      {children}
      {endIcon && <span data-icon='inline-end'>{endIcon}</span>}
    </>
  );

  if (component && component !== 'button') {
    return createElement(
      component,
      {
        className: cn(
          'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
          variant === 'contained' && 'bg-primary text-primary-foreground hover:bg-primary/80',
          variant === 'outlined' && 'border border-border hover:bg-muted',
          variant === 'text' && 'hover:bg-muted',
          sizeClassName[size],
          fullWidth && 'w-full',
          className
        ),
        ...rest
      } as never,
      content
    );
  }

  return (
    <DesignSystemButton
      className={cn(sizeClassName[size], fullWidth && 'w-full', className)}
      disabled={loading || rest.disabled}
      variant={variantMap[variant]}
      {...rest}
    >
      {content}
    </DesignSystemButton>
  );
};

Button.displayName = 'Button';

export default Button;
